import { BLEED, PAPER_4R, resolvePrintOrientation } from './geometry'
import type { ImageOrientation, SlotOrientation } from './types'

/**
 * Print geometry for the browser's own print path.
 *
 * This replaces citaria-print.exe, and the translation is not one-to-one — the
 * difference is worth understanding before touching any number here.
 *
 * The .NET helper drew into `e.PageBounds`, inset by (6,4) hundredths of an inch
 * for Portrait and (4,6) for Landscape:
 *
 *     e.Graphics.DrawImage(image, offX, offY,
 *                          bounds.Width - offX * 2, bounds.Height - offY * 2)
 *
 * On a borderless dye-sub driver `PageBounds` is REPORTED LARGER THAN THE MEDIA,
 * so that inset still over-filled a 4x6 sheet — which is where the edge-to-edge
 * print came from. There is no such thing in CSS: `@page { size: 4in 6in }` is
 * the sheet, full stop, and the same positive inset would leave a white border
 * the operator would see on the first print.
 *
 * So the inset is kept, but SIGNED and configurable:
 *
 *   inset > 0  the image sits inside the sheet, leaving a white margin
 *   inset = 0  the image exactly fills the sheet
 *   inset < 0  the image over-fills — the borderless bleed, and what a 4R
 *              dye-sub normally wants
 *
 * The default is the helper's own (6,4)/(4,6), because that is the number that
 * has been through a real printer; it is the honest starting point even though
 * most operators will end up dialling it negative. `calibrationInsets` drives
 * the Settings test sheet, which is what replaces the `--dry-run` check that
 * used to make this verifiable without spending paper.
 */

/** Hundredths of an inch, as PrinterSettings and the WPF app express paper. */
const PER_INCH = 100

/** The helper's ported inset, in inches. Portrait (0.06, 0.04); Landscape swaps. */
export const DEFAULT_INSET_IN = {
  Portrait: { x: BLEED.Portrait.x / PER_INCH, y: BLEED.Portrait.y / PER_INCH },
  Landscape: { x: BLEED.Landscape.x / PER_INCH, y: BLEED.Landscape.y / PER_INCH },
} as const

/** 4R as inches: the `@page size` the browser is asked for. */
export const PAPER_4R_IN = {
  width: PAPER_4R.width / PER_INCH,
  height: PAPER_4R.height / PER_INCH,
} as const

export interface Inset {
  /** Inches inset on the left and right edges. Negative bleeds off the sheet. */
  x: number
  /** Inches inset on the top and bottom edges. Negative bleeds off the sheet. */
  y: number
}

export interface PrintPageGeometry {
  /** The orientation actually sent to the printer, after `invertOrientation`. */
  orientation: SlotOrientation
  /** `@page { size: <pageWidthIn>in <pageHeightIn>in }`. */
  pageWidthIn: number
  pageHeightIn: number
  /** The image box, in inches from the page's top-left. Negative when bleeding. */
  leftIn: number
  topIn: number
  widthIn: number
  heightIn: number
}

/**
 * Where the composite goes on the sheet.
 *
 * `inset` is given in PORTRAIT terms and swapped for Landscape, so one setting
 * behaves the way the helper's two constants did. `invertOrientation` still
 * flips the orientation BEFORE the inset is chosen, so it swaps x and y too.
 */
export function printPageGeometry(
  imageOrientation: ImageOrientation,
  invertOrientation: boolean,
  inset?: Inset,
): PrintPageGeometry {
  const orientation = resolvePrintOrientation(imageOrientation, invertOrientation)

  // `inset` is the Portrait pair. Landscape swaps it, which is the whole reason
  // the helper carried two constants rather than one: (6,4) becomes (4,6), so
  // the wider edge always takes the smaller inset.
  const base = inset ?? DEFAULT_INSET_IN.Portrait
  const { x, y } = orientation === 'Portrait' ? base : { x: base.y, y: base.x }

  // Landscape is the same sheet turned, not a different sheet.
  const pageWidthIn = orientation === 'Portrait' ? PAPER_4R_IN.width : PAPER_4R_IN.height
  const pageHeightIn = orientation === 'Portrait' ? PAPER_4R_IN.height : PAPER_4R_IN.width

  return {
    orientation,
    pageWidthIn,
    pageHeightIn,
    leftIn: x,
    topIn: y,
    widthIn: pageWidthIn - x * 2,
    heightIn: pageHeightIn - y * 2,
  }
}

/** Rounds to a hundredth of an inch, the unit every number here started in. */
export function inches(value: number): string {
  return `${Math.round(value * PER_INCH) / PER_INCH}in`
}

/**
 * Insets the calibration sheet steps through, in inches.
 *
 * Six values from a 0.06in margin to a 0.06in bleed. One sheet shows the whole
 * useful range, so an operator finds their printer's number by looking rather
 * than by printing six times.
 */
export function calibrationInsets(): number[] {
  return [0.06, 0.03, 0, -0.02, -0.04, -0.06]
}

/**
 * The widest inset the settings field will accept, in inches.
 *
 * A 4R sheet is 4x6, so half an inch either way is already an extreme margin or
 * an extreme bleed. The cap is here to catch a typo — "5" instead of "0.5" —
 * before it reaches paper, not to express a real limit.
 */
export const MAX_INSET_IN = 0.5

/**
 * Parses a typed inset, or null when the text is not a number.
 *
 * Null rather than a fallback number because the caller has to be able to tell
 * "leave it alone" from "the operator meant zero". `Number('')` is 0 and passes
 * `Number.isFinite`, which is exactly how an emptied field used to commit a
 * ZERO inset — silently swapping a bleed for an edge-to-edge fill, on the one
 * setting CLAUDE.md §9 says costs physical paper to get wrong.
 *
 * A comma is accepted as the decimal separator. The console runs in Indonesian,
 * where that is the normal way to write it, and an `input[type=number]` reports
 * a comma-typed value as the empty string — so the field is plain text and the
 * parsing happens here.
 */
export function parseInset(text: string): number | null {
  const normalised = text.trim().replace(',', '.')
  if (normalised === '' || normalised === '-' || normalised === '.') return null

  const value = Number(normalised)
  if (!Number.isFinite(value)) return null

  const clamped = Math.max(-MAX_INSET_IN, Math.min(MAX_INSET_IN, value))
  // Hundredths of an inch is the unit every print number here started in; this
  // only trims float noise like 0.060000000000000005.
  return Math.round(clamped * 1000) / 1000
}
